import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { HelpCircle, Check, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { EntryEditor } from "./EntryEditor";
import { API_BASE } from "./imageUtils";

const QuickAnswers = ({ e, res, customers, patch }) => {
  const nameIssue = ["suggested", "unmatched"].includes(res.match_type);
  const readIssue = (e.uncertain || e.overwritten) && !e.confirmed;
  const opts = [...new Set([e.tons_text, ...(e.alternatives || [])].filter(Boolean))];
  return (
    <div className="space-y-3">
      {readIssue && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-700 w-full sm:w-auto">The correct quantity is:</span>
          {opts.map((o) => <Button key={o} variant="outline" className="h-11 text-base" data-testid={`q-pick-${e.id}-${o}`} onClick={() => patch({ tons_text: o, confirmed: true })}>{o}</Button>)}
          {e.date && e.tons != null && e.customer_read && <Button className="h-11 bg-emerald-600 hover:bg-emerald-700" data-testid={`q-confirm-${e.id}`} onClick={() => patch({ confirmed: true })}><Check className="h-4 w-4 mr-1" />Reading is correct</Button>}
        </div>
      )}
      {nameIssue && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-700 w-full sm:w-auto">Which website customer is this?</span>
          {res.suggestion && <Button variant="outline" className="h-11" data-testid={`q-accept-name-${e.id}`} onClick={() => patch({ matched_customer: res.suggestion })}>Yes, {res.suggestion}</Button>}
          <select data-testid={`q-choose-name-${e.id}`} className="h-11 rounded-md border bg-white text-base px-2" value="" onChange={(v) => v.target.value && patch({ matched_customer: v.target.value })}>
            <option value="">Other customer…</option>{customers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button variant="ghost" className="h-11" data-testid={`q-new-name-${e.id}`} onClick={() => patch({ new_customer: true })}>Not on website (keep as written)</Button>
        </div>
      )}
    </div>
  );
};

export const QuestionsPanel = ({ sessionId, entries, resolution, customers, onChange }) => {
  const open = entries.filter((e) => resolution[e.id]?.issues?.length);
  const [i, setI] = useState(0);
  const [editing, setEditing] = useState(false);
  if (!open.length) return null;
  const idx = Math.min(i, open.length - 1);
  const e = open[idx];
  const res = resolution[e.id];
  const patch = async (body) => {
    try { await api.patch(`/verification/entries/${e.id}`, body); toast.success("Answer saved"); onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this line? Use this only if it is not a sale.")) return;
    try { await api.delete(`/verification/entries/${e.id}`); onChange(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <section data-testid="questions-panel" className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-chivo font-black text-lg flex items-center gap-2 text-amber-900"><HelpCircle className="h-5 w-5" />Questions for you (<span data-testid="questions-left">{open.length}</span> left)</h3>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" data-testid="q-prev" className="h-10 w-10" disabled={idx === 0} onClick={() => { setI(idx - 1); setEditing(false); }}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-xs text-amber-900 px-1">{idx + 1}/{open.length}</span>
          <Button size="icon" variant="outline" data-testid="q-next" className="h-10 w-10" disabled={idx >= open.length - 1} onClick={() => { setI(idx + 1); setEditing(false); }}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div data-testid={`question-${e.id}`} className="bg-white rounded-lg border p-3 space-y-3">
        <div className="text-sm"><b>{e.plant}</b> · page {e.page ?? "manual"} · {e.date ? fmtDate(e.date) : "date unclear"} · <b>{e.customer_read || "?"}</b> — {e.tons_text || "?"}</div>
        <ul className="text-sm text-amber-800 list-disc pl-5">{res.issues.map((x) => <li key={x}>{x}</li>)}</ul>
        {e.note && <p className="text-xs text-slate-500">AI note: {e.note}</p>}
        {e.has_crop ? <img data-testid={`q-crop-${e.id}`} src={`${API_BASE}/verification/entries/${e.id}/crop`} alt="Handwritten line" className="max-w-full max-h-52 rounded border" />
          : <p className="text-xs text-slate-400">No picture available for this line.</p>}
        {editing ? (
          <EntryEditor key={e.id} entry={e} sessionId={sessionId} plant={e.plant} customers={customers} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); onChange(); }} />
        ) : (
          <>
            <QuickAnswers e={e} res={res} customers={customers} patch={patch} />
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" className="h-11" data-testid={`q-edit-${e.id}`} onClick={() => setEditing(true)}><Pencil className="h-4 w-4 mr-1" />Edit name, date or quantity</Button>
              <Button variant="ghost" className="h-11 text-red-600" data-testid={`q-delete-${e.id}`} onClick={remove}>Not a sale line — remove</Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
